import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  AppBar, Toolbar, Typography, Container, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper, Select,
  MenuItem, ButtonGroup, FormControl, InputLabel, Box, CircularProgress, IconButton, Modal
} from '@mui/material';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { parse, isValid } from 'date-fns';
import { auth } from './firebaseConfig';
import { onAuthStateChanged, signOut, SAMLAuthProvider, signInWithRedirect } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions"; // <-- Add Functions import
import './App.css';
import logo from './logo.png';

// --- Configuration ---
const SAML_PROVIDER_ID = 'saml.google'; // REPLACE if needed
const APP_BAR_HEIGHT = 64;
const PAGE_SIZE = 50; // Match backend page size
// --- End Configuration ---

// Function URL (replace if your region/project ID differs) - Updated to new function name
const GET_SIGNED_URL_FUNCTION_URL = "https://us-central1-pre-intake-app.cloudfunctions.net/generateSignedGcsUrl";

function App() {
  // --- State ---
  const [forms, setForms] = useState([]);
  const [facility, setFacility] = useState('');
  const [search, setSearch] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [lastUpdateType, setLastUpdateType] = useState('none');
  const [sortConfig, setSortConfig] = useState({ key: 'bookingDateTime', direction: 'descending' });
  const [dateFilter, setDateFilter] = useState('today');
  const [defaultFacility, setDefaultFacility] = useState(() => localStorage.getItem('defaultFacility') || '');
  const [lastDocId, setLastDocId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [generatingUrlFor, setGeneratingUrlFor] = useState(null); // Track which form's URL is being generated
  // --- End State ---

  // --- Refs ---
  const observer = useRef();
  // --- End Refs ---

  // --- Initialize facility state ---
  useEffect(() => {
    setFacility(defaultFacility);
  }, [defaultFacility]);

  // --- Helper: Title Case ---
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // --- Auth Listener ---
  useEffect(() => {
    console.log("Setting up onAuthStateChanged listener.");
    setAuthLoading(true);
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("Auth State Changed. User:", currentUser ? currentUser.email : 'None');
      setUser(currentUser);
      if (authLoading) {
        console.log("First auth state received. Setting authLoading to false.");
        setAuthLoading(false);
      }
    });
    return () => {
      console.log("Cleaning up onAuthStateChanged listener.");
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Auto Login ---
  useEffect(() => {
    // Only trigger auto-login if NOT in the process of logging out
    if (!authLoading && !user && !isLoggingOut) {
      console.log("Initial auth check complete, no user found, and not logging out. Triggering login.");
      handleLogin();
    } else if (isLoggingOut) {
      console.log("Skipping auto-login because logout is in progress.");
    }
  }, [authLoading, user, isLoggingOut]); // Add isLoggingOut to dependency array

  // --- Fetch Forms Function ---
  const fetchForms = useCallback(async ({ loadMore = false, updateType = 'auto', dateFilterOverride = null } = {}) => {
    const currentFilter = dateFilterOverride ?? dateFilter; // Use override if provided

    if (!auth.currentUser) {
      console.log("fetchForms aborted: No current user.");
      return;
    }
    if (isLoading || (loadMore && isFetchingMore)) {
      console.log("fetchForms aborted: Already fetching.");
      return;
    }

    console.log(`fetchForms called: ${loadMore ? 'Loading More' : 'Initial Load/Refresh'} (Filter: ${currentFilter})`);
    let fetchedData = null; // To store data for processing

    // Set loading states
    if (loadMore) {
      setIsFetchingMore(true);
    } else {
      setIsLoading(true);
      setIsFetchingMore(false); // Ensure this is false for initial load/refresh
      // Handle manual refresh state resets *within* the initial load block
      if (updateType === 'manual') {
        console.log("Manual refresh triggered, resetting pagination for first page fetch.");
        setLastDocId(null);
        setHasMore(true); // Reset hasMore on manual refresh
        setForms([]);
      }
    }

    try {
      const token = await auth.currentUser.getIdToken();
      console.log("Successfully retrieved Firebase ID token.");

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("REACT_APP_API_URL environment variable is not set!");
        throw new Error("API URL is not configured. Build process might be missing environment variables.");
      }
      const queryParams = new URLSearchParams();
      if (facility) queryParams.append('facility', facility);
      if (sortConfig.key) {
       // --- Determine effective sort direction ---
       // Always sort DESCENDING when 'Today' filter is active to get recent items first
       // Otherwise, use the user-selected sort direction
       const effectiveSortDirection = currentFilter === 'today' ? 'desc' : (sortConfig.direction === 'descending' ? 'desc' : 'asc');
       console.log(`DEBUG: Effective sort direction for fetch: ${effectiveSortDirection} (Filter: ${currentFilter}, UserSort: ${sortConfig.direction})`);

       queryParams.append('sortBy', sortConfig.key);
       queryParams.append('sortDirection', effectiveSortDirection);
     }
     if (loadMore && lastDocId) queryParams.append('startAfter', lastDocId);

      const queryString = queryParams.toString();
      const fetchUrl = `${apiUrl}/api/forms${queryString ? `?${queryString}` : ''}`;
      const fetchOptions = { headers: { 'Authorization': `Bearer ${token}` } };

      console.log(`Attempting fetch to: ${fetchUrl}`);
      const response = await fetch(fetchUrl, fetchOptions);

      if (response.status === 401 || response.status === 403) {
        console.error(`Authentication/Authorization error: ${response.status} ${response.statusText}`);
        handleLogout(); // Trigger logout on auth error
        throw new Error(`Auth error: ${response.status}`);
      }
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      fetchedData = await response.json(); // Assign fetched data

      if (fetchedData && fetchedData.forms) {
        if (loadMore) {
          setForms(prevForms => [...prevForms, ...fetchedData.forms]);
          console.log(`Appended ${fetchedData.forms.length} new forms.`);
        } else {
          setForms(fetchedData.forms);
          console.log(`Set ${fetchedData.forms.length} initial forms.`);
        }
        setLastDocId(fetchedData.lastDocId);
        setLastUpdated(new Date());
        setLastUpdateType(updateType);

        // --- Correctly set hasMore state ---
        const receivedLessThanFullPage = fetchedData.forms.length < PAGE_SIZE;
        setHasMore(fetchedData.hasMore);

        console.log(`Forms fetched successfully (Type: ${updateType}, HasMore: ${fetchedData.hasMore}, LastDocId: ${fetchedData.lastDocId})`);
        // --- End correctly set hasMore state ---

      } else {
        console.error("Received invalid data structure from backend:", fetchedData);
        if (!loadMore) setForms([]);
        setHasMore(false);
      }

    } catch (error) {
      console.error("Failed to fetch forms:", error.message || error);
      // Don't automatically set hasMore to false on all errors,
      // only if the fetch itself failed or returned bad data.
      // If it was an auth error handled above, hasMore state remains until logout clears it.
      if (!(error.message.includes("Auth error"))) {
         setHasMore(false);
      }
    } finally {
      // Reset loading states
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  }, [user, facility, sortConfig, lastDocId, hasMore, isLoading, isFetchingMore, dateFilter]); // Added dateFilter
  // --- End Fetch Forms Function ---


  // --- Data Fetching Trigger (Initial Load / Filter Change) ---
  useEffect(() => {
    if (user) {
      console.log("User/filters changed. Fetching initial data.");
      setForms([]);
      setLastDocId(null);
      setHasMore(true); // Reset hasMore optimistically
      fetchForms({ updateType: 'auto', loadMore: false });
    } else {
      console.log("User is logged out. Clearing forms and pagination state.");
      setForms([]);
      setLastUpdated(null);
      setLastDocId(null);
      setHasMore(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, facility, sortConfig]); // Exclude fetchForms
  // --- End Data Fetching Trigger ---


  // --- Infinite Scroll Logic ---
  const lastElementRef = useCallback(node => {
    if (isLoading || isFetchingMore) return; // Don't observe if already loading/fetching
    if (observer.current) observer.current.disconnect(); // Disconnect previous

    observer.current = new IntersectionObserver(entries => {
      // Check if intersecting, hasMore is true, and not currently fetching more
      if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
        console.log("Last element visible, fetching more data...");
        fetchForms({ loadMore: true, updateType: 'auto' });
      }
    });

    if (node) observer.current.observe(node); // Observe the new last element

  }, [isLoading, isFetchingMore, hasMore, fetchForms]); // Added fetchForms back as it's called inside
  // --- End Infinite Scroll Logic ---


  // --- Client-Side Filtering and Date Parsing ---
  const processedAndFilteredForms = useMemo(() => {
    console.log(`Memoizing: Processing ${forms.length} forms from state...`);
    let filteredItems = [...forms];

    filteredItems = filteredItems.map(form => {
        let parsedDate = null;
        const timestampString = form.bookingTimestampUTC;
        if (timestampString) {
            const dateObj = new Date(timestampString);
            if (!isNaN(dateObj.getTime())) parsedDate = dateObj;
            else console.warn(`Could not parse UTC timestamp for ID ${form.id}: ${timestampString}`);
        }
        return { ...form, bookingDateTime: parsedDate };
    });

    const today = new Date();
    const todayStartTimestamp = today.setHours(0, 0, 0, 0);

    filteredItems = filteredItems.filter((form) => {
      if (search && !`${toTitleCase(form.firstName)} ${toTitleCase(form.lastName)}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (dateFilter === 'today') {
        const formDate = form.bookingDateTime;
        if (!formDate) return false;
        const formDateStartTimestamp = new Date(formDate).setHours(0, 0, 0, 0);
        if (formDateStartTimestamp !== todayStartTimestamp) return false;
      }
      return true;
    });

    console.log("Processed forms after client-side filtering:", filteredItems.length);
    return filteredItems;
  }, [forms, search, dateFilter]);
  // --- End Client-Side Filtering ---


  // --- Calculate Counts ---
  const { totalCompleted, totalDeferred } = useMemo(() => {
    let completedCount = 0;
    let deferredCount = 0;
    processedAndFilteredForms.forEach(form => {
      const isDeferred = form.transferToHospital === "Yes: Transfer to Hospital Now";
      if (isDeferred) deferredCount++;
      else completedCount++;
    });
    return { totalCompleted: completedCount, totalDeferred: deferredCount };
  }, [processedAndFilteredForms]);
  // --- End Calculate Counts ---


  // --- Authentication Handlers ---
  const handleLogin = () => {
    const provider = new SAMLAuthProvider(SAML_PROVIDER_ID);
    console.log(`Initiating SAML sign-in with redirect for provider: ${SAML_PROVIDER_ID}`);
    signInWithRedirect(auth, provider)
      .catch((error) => {
        console.error("SAML Redirect Initiation Error:", error);
        alert(`Login initiation failed: ${error.message} - ${error.code}`);
        setAuthLoading(false);
      });
  };

  const handleLogout = () => {
    console.log("Initiating sign-out...");
    setIsLoggingOut(true);
    signOut(auth)
      .then(() => {
        console.log("Sign-out successful. Redirecting...");
        window.location.href = 'https://team.firstclasshealthcare.com'; // Or your desired logout destination
      })
      .catch((error) => {
        console.error("Sign Out Error:", error);
        alert(`Logout failed: ${error.message}`);
        setIsLoggingOut(false);
      });
  };
  // --- End Authentication Handlers ---


  // --- Other Handlers ---
  const handleFacilityChange = (event) => {
    setFacility(event.target.value);
  };

  const handleSetDefaultFacility = (facilityName, event) => {
    event.stopPropagation();
    setDefaultFacility(currentDefault => {
      const newDefault = currentDefault === facilityName ? '' : facilityName;
      console.log(`Setting default facility to: '${newDefault}'`);
      return newDefault;
    });
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    // Reset pagination and fetch fresh data for the new date filter
    setForms([]);
    setLastDocId(null);
    setHasMore(true);
    fetchForms({ updateType: 'manual', loadMore: false, dateFilterOverride: newFilter });
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // --- Handle View Click (Signed URL) ---
  const handleViewClick = async (gcsFilePath, formId) => {
    if (!gcsFilePath) {
      console.error("No GCS file path provided for form ID:", formId);
      alert("Could not find the document path for this form.");
      return;
    }
    if (generatingUrlFor === formId) {
      console.log("Already generating URL for form ID:", formId);
      return; // Prevent double clicks
    }

    // --- Explicit Auth Check ---
    if (!auth.currentUser) {
      console.error("User is not authenticated. Cannot call function.");
      alert("Authentication error. Please refresh the page or log in again.");
      return;
    }
    // --- End Explicit Auth Check ---

    console.log(`Requesting signed URL for form ID ${formId}, path: ${gcsFilePath}`);
    setGeneratingUrlFor(formId); // Indicate loading state for this specific button

    // --- Force Token Refresh ---
    try {
      console.log("Forcing ID token refresh before calling function...");
      await auth.currentUser.getIdToken(true); // Force refresh
      console.log("ID token refreshed.");
    } catch (refreshError) {
      console.error("Error forcing ID token refresh:", refreshError);
      alert("Failed to refresh authentication. Please try again.");
      setGeneratingUrlFor(null); // Reset loading state
      return;
    }
    // --- End Force Token Refresh ---

    try {
      // Get the latest ID token
      const idToken = await auth.currentUser.getIdToken();

      // Make a standard fetch POST request
      const response = await fetch(GET_SIGNED_URL_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filePath: gcsFilePath }),
      });

      if (!response.ok) {
        // Handle HTTP errors (like 401, 404, 500)
        const errorText = await response.text();
        console.error(`HTTP error calling getSignedDocumentUrl: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Failed to get signed URL: ${response.statusText || errorText}`);
      }

      const result = await response.json();
      const signedUrl = result?.signedUrl;

      if (signedUrl) {
        console.log(`Received signed URL for ${formId} via HTTP request. Triggering download.`);
        const link = document.createElement('a');
        link.href = signedUrl;
        link.target = '_blank'; // Open in a new tab
        link.rel = 'noopener noreferrer'; // Security best practice
        // Extract filename from gcsFilePath
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Cloud function did not return a valid signedUrl in JSON response for form ID:", formId, "Result:", result);
        throw new Error("Cloud function did not return a valid signed URL.");
      }
    } catch (error) {
      console.error(`Error fetching signed URL for ${gcsFilePath} (Form ID: ${formId}):`, error);
      // Basic error message, could be enhanced based on response status if needed
      alert(`Failed to generate secure view link: ${error.message}`);
    } finally {
      setGeneratingUrlFor(null); // Reset loading state regardless of success/failure
    }
  };
  // --- End Other Handlers ---

  // --- Render Logic ---
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress /> <Typography sx={{ ml: 2 }}>Loading Authentication...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* AppBar */}
      <AppBar position="sticky" top={0} sx={{ backgroundColor: 'white', color: 'black', zIndex: 1100 }} elevation={1}>
        <Toolbar>
          <img src={logo} alt="FirstClass Logo" style={{ marginRight: '16px', height: '40px' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 'bold' }}>
            Pre-Intake Dashboard
          </Typography>
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>{user.email}</Typography>
              <Button color="inherit" onClick={handleLogout}>Logout</Button>
            </Box>
          ) : (
            <Button color="inherit" onClick={handleLogin}>Login</Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      {user ? (
        <>
          {/* Control Bar */}
          <Paper sx={{ position: 'sticky', top: `${APP_BAR_HEIGHT}px`, zIndex: 1090, p: 2, m: 0, backgroundColor: '#f9f9f9', borderBottom: '1px solid', borderColor: 'divider' }}>
            {/* Top Row Controls */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', mb: 1 }}>
              <Button variant="contained" color="primary" onClick={() => setIsFormModalOpen(true)} size="large" sx={{ width: { xs: '100%', sm: 'auto' }, order: { xs: -1, sm: 0 }, mb: { xs: 1, sm: 0 } }}>
                Complete Pre-Intake Form
              </Button>
              <FormControl sx={{ width: { xs: '100%', sm: 'auto' }, flexGrow: { xs: 0, sm: 1 } }} size="small">
                <InputLabel id="facility-select-label">Facility</InputLabel>
                <Select labelId="facility-select-label" value={facility} label="Facility" onChange={handleFacilityChange} variant="filled">
                  <MenuItem value=""><em>All Facilities</em></MenuItem>
                  {[...new Set(forms.map(form => form.facilityName).filter(Boolean))].sort().map(facilityName => (
                    <MenuItem key={facilityName} value={facilityName}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <span style={{ flexGrow: 1 }}>{facilityName}</span>
                        <IconButton size="small" onClick={(event) => handleSetDefaultFacility(facilityName, event)} aria-label={defaultFacility === facilityName ? "Unset as default facility" : "Set as default facility"}>
                          {defaultFacility === facilityName ? <Star fontSize="inherit" color="primary" /> : <StarBorder fontSize="inherit" />}
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Search Name" variant="filled" size="small" sx={{ width: { xs: '100%', sm: 'auto' }, flexGrow: { xs: 0, sm: 1 } }} onChange={handleSearchChange} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 'auto' } }}>
                <IconButton color="primary" onClick={() => fetchForms({ loadMore: false, updateType: 'manual' })} disabled={isLoading || isFetchingMore} aria-label="Refresh data" size="small">
                  {isLoading && !isFetchingMore ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                </IconButton>
                {lastUpdated && (<Typography variant="caption" color="textSecondary">{lastUpdateType === 'auto' ? 'Auto-updated: ' : 'Last updated: '} {lastUpdated.toLocaleTimeString()}</Typography>)}
              </Box>
            </Box>
            {/* Bottom Row Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, mt: 1, mb: 1, gap: 1 }}>
              <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 1, sm: 0 } }}>
                <Typography variant="body2" color="textSecondary">Total Preintake Completed: <strong>{totalCompleted}</strong></Typography>
                <Typography variant="body2" color="textSecondary">Total Deferrals: <strong>{totalDeferred}</strong></Typography>
              </Box>
              <ButtonGroup variant="outlined" aria-label="Date filter group">
                <Button onClick={() => handleDateFilterChange('today')} variant={dateFilter === 'today' ? 'contained' : 'outlined'}>Today</Button>
                <Button onClick={() => handleDateFilterChange('all')} variant={dateFilter === 'all' ? 'contained' : 'outlined'}>All</Button>
              </ButtonGroup>
            </Box>
          </Paper>

          {/* Scrollable Table Area */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', pt: 2 }}>
            <Container maxWidth="xl" sx={{ mt: 0 }}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 750 }} aria-label="simple table" stickyHeader>
                  <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <TableRow>
                      {[
                        { id: 'facilityName', label: 'Facility' }, { id: 'firstName', label: 'First Name' }, { id: 'lastName', label: 'Last Name' },
                        { id: 'dob', label: 'DOB', hideOnMobile: true }, { id: 'bookingDateTime', label: 'Booking Date/Time' },
                        { id: 'notes', label: 'Notes', disableSorting: true, hideOnMobile: true }, { id: 'pdfLink', label: 'Pre-Intake Form', disableSorting: true },
                      ].map((headCell) => (
                        <TableCell key={headCell.id} align="left" padding="normal" sortDirection={sortConfig.key === headCell.id ? sortConfig.direction : false}
                          sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', cursor: headCell.disableSorting ? 'default' : 'pointer', display: headCell.hideOnMobile ? { xs: 'none', sm: 'table-cell' } : 'table-cell' }}
                          onClick={() => !headCell.disableSorting && requestSort(headCell.id)}>
                          {headCell.disableSorting ? headCell.label : (
                            <TableSortLabel active={sortConfig.key === headCell.id} direction={sortConfig.key === headCell.id ? (sortConfig.direction === 'ascending' ? 'asc' : 'desc') : 'asc'}>
                              {headCell.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading && !isFetchingMore && (<TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /><Typography sx={{ ml: 1, display: 'inline-block' }}>Loading forms...</Typography></TableCell></TableRow>)}
                    {!isLoading && processedAndFilteredForms.map((form, index) => {
                      const isLastElement = processedAndFilteredForms.length === index + 1;
                      // Use the field name provided by the backend API for deferral
                      const isDeferred = form.transferToHospital === "Yes: Transfer to Hospital Now";
                      // Use the field name provided by the backend API for mental health crisis
                      const isMentalHealthAlert = form.mentalHealthCrisisStatus === "Yes";
                      let rowStyle = {};

                      // Apply styles: Red for deferred, Yellow for mental health crisis (if not deferred)
                      if (isDeferred) {
                        rowStyle = { backgroundColor: 'rgba(255, 0, 0, 0.1)' }; // Red background
                      } else if (isMentalHealthAlert) {
                        rowStyle = { backgroundColor: 'rgba(255, 255, 0, 0.3)' }; // Yellow background
                      }

                      return (
                        <TableRow ref={isLastElement ? lastElementRef : null} key={form.id} sx={rowStyle}>
                          {/* Display (Deferred) or (mental health) based on the conditions */}
                          <TableCell>{form.facilityName}{isDeferred && (<Typography sx={{ display: 'inline', marginLeft: '5px', fontSize: '0.75rem', fontWeight: 'bold', color: 'red' }}>(Deferred)</Typography>)}{isMentalHealthAlert && !isDeferred && (<Typography sx={{ display: 'inline', marginLeft: '5px', fontSize: '0.75rem', fontWeight: 'bold', color: '#b2b200' }}>(mental health)</Typography>)}</TableCell>
                          <TableCell>{toTitleCase(form.firstName)}</TableCell>
                          <TableCell>{toTitleCase(form.lastName)}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{form.dob}</TableCell>
                          <TableCell>{form.bookingDateTime ? `${form.bookingDateTime.toLocaleDateString()} ${form.bookingDateTime.toLocaleTimeString()}` : form.bookingTimestampUTC || ''}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><span title={form.notes || ''}>{(form.notes && form.notes.length > 50) ? `${form.notes.substring(0, 50)}...` : form.notes || ''}</span></TableCell>
                          <TableCell>
                            <Button
                              color="primary"
                              onClick={() => handleViewClick(form.pdfGcsPath, form.id)} // Use onClick with the GCS path from form data
                              disabled={generatingUrlFor === form.id || !form.pdfGcsPath} // Disable if loading this URL or if path is missing
                            >
                              {generatingUrlFor === form.id ? <CircularProgress size={20} /> : 'View'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Only show loading more indicator if fetching more, more might exist, AND we are NOT on the 'today' tab */}
                    {isFetchingMore && hasMore && dateFilter !== 'today' && (
                      <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={20} /><Typography variant="caption" sx={{ ml: 1, display: 'inline-block' }}>Loading more...</Typography></TableCell></TableRow>
                    )}
                    {!isLoading && !isFetchingMore && forms.length === 0 && (<TableRow><TableCell colSpan={7} align="center"><Typography variant="body2">No forms found matching your criteria.</Typography></TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
          </Box>
        </>
      ) : (
        <Container sx={{ textAlign: 'center', mt: 5, p: 3 }}><Typography variant="h5">{isLoggingOut ? "Logging out..." : "Authenticating..."}</Typography></Container>
      )}

      {/* Cognito Form Modal */}
      <Modal open={isFormModalOpen} onClose={(event, reason) => { if (reason !== 'backdropClick') setIsFormModalOpen(false); }} aria-labelledby="pre-intake-form-modal-title" aria-describedby="pre-intake-form-modal-description" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ position: 'relative', width: '90%', height: '90%', maxWidth: '1000px', maxHeight: '80vh', bgcolor: 'background.paper', boxShadow: 24, p: 2, overflow: 'hidden', border: '3px solid', borderColor: 'primary.light' }}>
          <IconButton aria-label="close pre-intake form" onClick={() => setIsFormModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[600], zIndex: 1, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}><CloseIcon /></IconButton>
          <Typography id="pre-intake-form-modal-title" variant="h5" component="h2" sx={{ textAlign: 'center', pt: 1, pb: 1, fontWeight: 'bold' }}>Pre-Intake Form</Typography>
          <iframe src="https://www.cognitoforms.com/f/O1aPyyQ6IkmbX50DeSxuTw/6" style={{ border: 0, width: '100%', height: 'calc(100% - 50px)' }} title="Pre-Intake Form"></iframe>
        </Paper>
      </Modal>

    </Box>
  );
}

export default App;
